import React from "react";
import {
  Box, Text, SimpleGrid, VStack, HStack, Button,
  Badge, useColorModeValue, Icon, Divider, Heading, Flex,
} from "@chakra-ui/react";
import { FaFileAlt, FaClock, FaLock, FaCheckCircle, FaTrophy, FaLayerGroup, FaChartBar } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useStudentContext } from "../B2CStudentContext";
import { useMemo } from "react";

const B2CMockExamsPage = () => {
  const navigate = useNavigate();
  const { entitlement_id, practice_set_id } = useParams();
  const { studentContext } = useStudentContext();

  const cardBg = useColorModeValue("purple.50", "gray.800");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const statBg = useColorModeValue("gray.50", "gray.750");
  const examReadyBg = useColorModeValue("purple.100", "purple.900");
  const examReadyBorder = useColorModeValue("purple.200", "purple.700");
  const txtColor = useColorModeValue("purple.700", "purple.200");

  const entitlement = useMemo(() => {
    return studentContext?.entitlements?.find(
      (e) => e.entitlement_id === Number(entitlement_id)
    );
  }, [studentContext, entitlement_id]);

  const practiceSet = entitlement?.practice_set;

  const handleOpenExam = (
    entitlement_id: number,
    practice_set_id: number,
    practice_set_exam_id: number
  ) => {
    const params = new URLSearchParams({
      entitlement_id: String(entitlement_id),
      practice_set_id: String(practice_set_id),
      assessment_type: "MOCK_EXAM",
      timer_enabled: "true",
      practice_set_exam_id: String(practice_set_exam_id),
    });
    navigate(`/practice/app/practice-attempt/attempt-instructions?${params.toString()}`);
  };

  if (!practiceSet) {
    return <Box p={10}><Text>Practice Set not found.</Text></Box>;
  }

  const sortedExams = [...practiceSet.exams].sort(
    (a, b) => a.sequence_number - b.sequence_number
  );

  // ── Hero-level stats, same shape as Section Practice ──
const totalExams = sortedExams.length;
const exhaustedCount = sortedExams.filter(
  e => e.max_attempts != null && (e.remaining_attempts ?? 0) <= 0
).length;
const availableCount = totalExams - exhaustedCount;

// Exams differ in length, so show a range instead of sampling the first exam
const durations = sortedExams
  .map(e => e.total_duration_minutes ?? 0)
  .filter(d => d > 0);
const minDuration = durations.length ? Math.min(...durations) : 0;
const maxDuration = durations.length ? Math.max(...durations) : 0;
const durationDisplay =
  durations.length === 0 ? "—"
  : minDuration === maxDuration ? `${minDuration}m`
  : `${minDuration}–${maxDuration}m`;


  const isSingleAttempt = sortedExams.every(
  e => e.max_attempts != null && e.max_attempts <= 1
);

const attemptedCount = sortedExams.filter(
  e => e.max_attempts != null && (e.remaining_attempts ?? 0) < e.max_attempts
).length;

const hasUnlimited = sortedExams.some(e => e.max_attempts == null);
const attemptsLeft = sortedExams.reduce(
  (sum, e) => sum + (e.max_attempts == null ? 0 : (e.remaining_attempts ?? 0)), 0
);

const heroStats = isSingleAttempt
  ? [
      { label: "Attempted", value: `${attemptedCount}/${totalExams}`, icon: FaCheckCircle },
      { label: "Duration", value: durationDisplay, icon: FaClock },
    ]
  : [
      { label: "Available", value: `${availableCount}/${totalExams}`, icon: FaCheckCircle },
      { label: "Attempts Left", value: hasUnlimited ? "∞" : String(attemptsLeft), icon: FaTrophy },
      { label: "Duration", value: durationDisplay, icon: FaClock },
    ];

  return (
    <Box maxW="1400px" mx="auto" p={6}>

      {/* ── Hero header, matched to Section Practice's structure, purple-toned ── */}
      <Box bg={cardBg} borderRadius="2xl" borderWidth="1px"
        borderColor={borderColor} p={6} mb={6}>
        <Flex align="start" gap={6} direction={{ base: "column", md: "row" }}>

          <VStack align="start" spacing={3} flex={1}>
            <HStack spacing={2}>
              <Badge colorScheme="purple" px={3} py={1} borderRadius="full">
                Mock Exams
              </Badge>
              {exhaustedCount > 0 && (
                <Badge colorScheme="red" px={3} py={1} borderRadius="full">
                  {exhaustedCount} exhausted
                </Badge>
              )}
            </HStack>

            <Text fontSize="2xl" fontWeight="bold">{practiceSet.title}</Text>

            <Text color={mutedText} fontSize="sm">
              {totalExams} timed mock exam{totalExams !== 1 ? "s" : ""}, each taken as a separate
              sitting with its own duration and attempt limit
            </Text>
          </VStack>

          <HStack spacing={4} flexShrink={0} align="stretch">
            {heroStats.map((stat) => (
              <Box key={stat.label} bg={statBg} borderRadius="xl"
                borderWidth="1px" borderColor={borderColor}
                p={4} textAlign="center" minW="90px">
                <Icon as={stat.icon} color="purple.500"
                  boxSize={4} mb={1} />
                <Text fontSize="xl" fontWeight="bold">{stat.value}</Text>
                <Text fontSize="xs" color={mutedText}>{stat.label}</Text>
              </Box>
            ))}
          </HStack>
        </Flex>
      </Box>

      {/* Recommendation banner -- unchanged */}
      <Box bg={examReadyBg} border="1px solid" borderColor={examReadyBorder}
        borderRadius="xl" p={5} mb={8}>
        <HStack spacing={3}>
          <Icon as={FaCheckCircle} color="purple.500" boxSize={5} flexShrink={0} />
          <Text fontSize="sm" color={txtColor}>
            <strong>Best results tip:</strong> Complete all practice sections before
            attempting a mock exam. Mock exams are timed and simulate real exam conditions —
            treat each attempt seriously.
            <br />
            Each exam below is a separate sitting. Check its description for how it is
            marked and when to expect results, as some exams may take longer to be scored.
          </Text>
        </HStack>
      </Box>

      {/* ...rest of the file (sortedExams.length === 0 check, SimpleGrid of exam cards) unchanged... */}

      {sortedExams.length === 0 ? (
        <Box textAlign="center" py={16} color={mutedText}>
          <Icon as={FaFileAlt} w={10} h={10} mb={4} />
          <Text>No mock exams available yet.</Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
          {sortedExams.map((exam, idx) => {
            const isUnlimited = exam.max_attempts == null;
            const exhausted = !isUnlimited && (exam.remaining_attempts ?? 0) <= 0;
            const remaining = exam.remaining_attempts ?? 0;
            const used = isUnlimited ? null : (exam.max_attempts ?? 0) - remaining;

            return (
            <Box
              key={exam.practice_set_exam_id}
              bg={cardBg}
              borderRadius="2xl"
              borderWidth="1px"
              borderColor={exhausted ? "red.200" : borderColor}
              p={6}
              position="relative"
              transition="box-shadow 0.2s"
              _hover={!exhausted ? { boxShadow: "lg" } : {}}
            >
              {/* Exam number badge */}
              <Badge
                position="absolute"
                top={4}
                right={4}
                colorScheme={exhausted ? "red" : "purple"}
                borderRadius="full"
                px={2}
                fontSize="xs"
                opacity={exhausted ? 0.65 : 1}
              >
                Exam {idx + 1}
              </Badge>

              <VStack align="start" spacing={4}>
                {/* Faded block: only the informational content */}
                <VStack align="start" spacing={4} w="100%" opacity={exhausted ? 0.65 : 1}>
                  <HStack spacing={3}>
                    <Icon
                      as={exhausted ? FaLock : FaFileAlt}
                      color={exhausted ? "red.400" : "purple.500"}
                      boxSize={5}
                    />
                    <Text fontWeight="bold" fontSize="lg" pr={12}>
                      {exam.title}
                    </Text>
                  </HStack>

                  {exam.description && (
                    <Text fontSize="sm" color={mutedText} lineHeight="1.6">
                      {exam.description}
                    </Text>
                  )}

                  <Divider />

                  <VStack align="start" spacing={2} w="100%">
                    <HStack spacing={2}>
                      <Icon as={FaClock} color={mutedText} boxSize={3} />
                      <Text fontSize="sm" color={mutedText}>
                        {exam.total_duration_minutes ?? 0} minutes
                      </Text>
                    </HStack>

                    <HStack spacing={2}>
                      <Text fontSize="sm" color={mutedText}>Attempts:</Text>
                      {isUnlimited ? (
                        <Badge colorScheme="green" fontSize="xs">Unlimited</Badge>
                      ) : exhausted ? (
                        <Badge colorScheme="red" fontSize="xs">
                          All {exam.max_attempts} used
                        </Badge>
                      ) : (
                        <HStack spacing={1}>
                          <Badge colorScheme="blue" fontSize="xs">{remaining} remaining</Badge>
                          <Text fontSize="xs" color={mutedText}>of {exam.max_attempts}</Text>
                        </HStack>
                      )}
                    </HStack>
                  </VStack>
                </VStack>

                {/* Always fully enabled */}
                <Button
                  width="100%"
                  variant="outline"
                  colorScheme="purple"
                  size="sm"
                  leftIcon={<FaChartBar />}
                  onClick={() =>
                    navigate(
                      `/practice/app/entitlement/${entitlement_id}/practice-set/${practice_set_id}/exam/${exam.practice_set_exam_id}/attempts`
                    )
                  }
                >
                  View Past Attempts
                </Button>

                <Button
                  width="100%"
                  colorScheme={exhausted ? "gray" : "purple"}
                  isDisabled={exhausted}
                  leftIcon={exhausted ? <FaLock /> : <FaFileAlt />}
                  onClick={() =>
                    handleOpenExam(
                      entitlement!.entitlement_id,
                      practiceSet.practice_set_id,
                      exam.practice_set_exam_id
                    )
                  }
                >
                  {exhausted ? "No Attempts Remaining" : "Start Exam"}
                </Button>
              </VStack>
            </Box>
            );
          })}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default B2CMockExamsPage;